import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { FileDown, Printer, ListChecks } from "lucide-react";
import { API_URL, formatCurrency } from "../../lib/utils";
import { buildCategoryTree, categoryPath } from "../../lib/categories";
import { promotionFor } from "../../lib/promotions";
import { printPriceList } from "../../lib/priceListPrint";

export const PriceListTab = ({ products, categories, headers, canInternal = false }) => {
  const [settings, setSettings] = useState({});
  const [promos, setPromos] = useState([]);
  const [selected, setSelected] = useState([]); // category ids (roots) — vazio = todas
  const [onlyAvailable, setOnlyAvailable] = useState(true);
  const [showNet, setShowNet] = useState(true);
  const [showDescription, setShowDescription] = useState(false);
  const [showImages, setShowImages] = useState(false);
  const [internal, setInternal] = useState(false);
  const [validUntil, setValidUntil] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    axios.get(`${API_URL}/settings`, { headers, withCredentials: true }).then((r) => setSettings(r.data || {})).catch(() => {});
    axios.get(`${API_URL}/promotions/active`, { headers, withCredentials: true }).then((r) => setPromos(r.data || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tree = useMemo(() => buildCategoryTree(categories), [categories]);
  const filtered = useMemo(() => {
    const rootOf = (cid) => tree.parentOf[cid] || cid;
    return products.filter((p) => (!onlyAvailable || p.is_available) && (selected.length === 0 || selected.includes(rootOf(p.category_id))));
  }, [products, onlyAvailable, selected, tree]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const exportPdf = () => {
    if (!filtered.length) return toast.error("Sem artigos para exportar");
    const ok = printPriceList({ products: filtered, categories, promos, settings, options: { showNet, showDescription, showImages, validUntil, note, internal: canInternal && internal, title: canInternal && internal ? "LISTA DE PREÇOS — VERSÃO INTERNA" : "LISTA DE PREÇOS" } });
    if (!ok) toast.error("O navegador bloqueou a janela — permita pop-ups para exportar");
    else toast.success(`Lista de preços com ${filtered.length} artigo(s) pronta — escolha "Guardar como PDF" na impressão`);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 min-w-0" data-testid="price-list-tab">
      <Card className="lg:col-span-1 min-w-0">
        <CardContent className="p-5 space-y-5">
          <div>
            <p className="font-semibold flex items-center gap-2"><ListChecks className="w-4 h-4" /> Categorias</p>
            <p className="text-xs text-muted-foreground mb-2">Nenhuma selecionada = todas</p>
            <div className="flex flex-wrap gap-1.5" data-testid="price-list-categories">
              {tree.roots.map((c) => (
                <button key={c.category_id} type="button" onClick={() => toggle(c.category_id)}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${selected.includes(c.category_id) ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`}
                  data-testid={`price-list-cat-${c.category_id}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            {[
              ["Só artigos disponíveis", onlyAvailable, setOnlyAvailable, "price-list-only-available"],
              ["Mostrar preço sem IVA", showNet, setShowNet, "price-list-show-net"],
              ["Incluir descrição do artigo", showDescription, setShowDescription, "price-list-show-description"],
              ["Incluir fotos (miniaturas)", showImages, setShowImages, "price-list-show-images"],
            ].map(([label, val, set, tid]) => (
              <div key={tid} className="flex items-center justify-between">
                <Label className="text-sm">{label}</Label>
                <Switch checked={val} onCheckedChange={set} data-testid={tid} />
              </div>
            ))}
            {canInternal && (
              <div className={`flex items-center justify-between rounded-lg border px-3 py-2 ${internal ? "border-red-500/40 bg-red-500/5" : ""}`}>
                <div>
                  <Label className="text-sm font-semibold">Versão interna</Label>
                  <p className="text-[11px] text-muted-foreground">Acrescenta custo, margem e stock atual · marca "USO INTERNO"</p>
                </div>
                <Switch checked={internal} onCheckedChange={setInternal} data-testid="price-list-internal" />
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Válida até (opcional)</Label>
            <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} data-testid="price-list-valid-until" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Nota de rodapé (opcional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex: Preços para take-away. Entregas com taxa adicional." data-testid="price-list-note" />
          </div>
          <Button onClick={exportPdf} className="w-full gap-2" data-testid="price-list-export-btn">
            <FileDown className="w-4 h-4" /> Exportar PDF ({filtered.length} artigos)
          </Button>
          <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Printer className="w-3 h-3" /> Abre o documento A4 pronto a imprimir; escolha "Guardar como PDF" no destino.</p>
        </CardContent>
      </Card>

      <Card className="lg:col-span-2 min-w-0 overflow-hidden">
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <p className="font-semibold">Pré-visualização</p>
            <Badge variant="outline" data-testid="price-list-count">{filtered.length} artigo(s)</Badge>
          </div>
          <div className="max-h-[560px] overflow-y-auto overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur text-xs uppercase text-muted-foreground">
                <tr><th className="text-left p-2 pl-4">Artigo</th><th className="text-left p-2">Categoria</th>{canInternal && internal && <><th className="text-right p-2">Custo</th><th className="text-right p-2">Margem</th><th className="text-center p-2">Stock</th></>}{showNet && <th className="text-right p-2">s/ IVA</th>}<th className="text-center p-2">IVA</th><th className="text-right p-2 pr-4">c/ IVA</th></tr>
              </thead>
              <tbody>
                {filtered.slice().sort((a, b) => categoryPath(categories, a.category_id).localeCompare(categoryPath(categories, b.category_id)) || a.name.localeCompare(b.name)).map((p) => {
                  const { price, promo, original } = promotionFor(p, promos, tree.parentOf);
                  const net = price / (1 + (Number(p.tax_rate) || 0) / 100);
                  const cost = Number(p.cost_price) || 0;
                  const margin = net > 0 ? ((net - cost) / net) * 100 : 0;
                  return (
                    <tr key={p.product_id} className="border-b last:border-0" data-testid={`price-list-row-${p.product_id}`}>
                      <td className="p-2 pl-4 font-medium">{p.name}{promo && <span className="ml-1.5 text-[10px] font-black bg-red-600 text-white px-1 py-px rounded">PROMO</span>}</td>
                      <td className="p-2 text-muted-foreground text-xs">{categoryPath(categories, p.category_id)}</td>
                      {canInternal && internal && (
                        <>
                          <td className="p-2 text-right font-mono text-muted-foreground">{formatCurrency(cost)}</td>
                          <td className={`p-2 text-right font-mono ${margin < 20 ? "text-red-600 font-semibold" : ""}`}>{margin.toFixed(1)}%</td>
                          <td className="p-2 text-center font-mono text-xs">{p.track_stock ? p.current_stock ?? 0 : "—"}</td>
                        </>
                      )}
                      {showNet && <td className="p-2 text-right font-mono text-muted-foreground">{formatCurrency(net)}</td>}
                      <td className="p-2 text-center font-mono text-xs">{Number(p.tax_rate ?? 14)}%</td>
                      <td className="p-2 pr-4 text-right font-mono font-semibold">{promo && <s className="text-muted-foreground font-normal text-xs mr-1">{formatCurrency(original)}</s>}{formatCurrency(price)}</td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Sem artigos para os filtros escolhidos</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
